import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from "lucide-react";
import { useHive } from "@/store/useHive";
import type { Toast } from "@/types";

const TONE: Record<Toast["tone"], { icon: any; color: string }> = {
  success: { icon: CheckCircle2, color: "#22c55e" },
  warning: { icon: AlertTriangle, color: "#f59e0b" },
  error: { icon: XCircle, color: "#f43f5e" },
  info: { icon: Info, color: "#3b82f6" },
};

function ToastRow({ toast }: { toast: Toast }) {
  const dismissToast = useHive((s) => s.dismissToast);
  const t = TONE[toast.tone];
  useEffect(() => {
    const id = setTimeout(() => dismissToast(toast.id), 4200);
    return () => clearTimeout(id);
  }, [toast.id, dismissToast]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 40, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.9 }}
      transition={{ type: "spring", stiffness: 380, damping: 30 }}
      className="pointer-events-auto group flex w-[340px] items-start gap-3 overflow-hidden rounded-xl border border-app-strong glass-strong p-3.5 shadow-window"
    >
      <span
        className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg"
        style={{ background: `${t.color}1f` }}
      >
        <t.icon size={16} style={{ color: t.color }} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-app">{toast.title}</div>
        {toast.body && <div className="mt-0.5 truncate text-xs text-app-2">{toast.body}</div>}
      </div>
      <button
        onClick={() => dismissToast(toast.id)}
        className="rounded p-0.5 text-app-2 opacity-0 transition hover:bg-surface-2 group-hover:opacity-100"
      >
        <X size={13} />
      </button>
      <motion.div
        className="absolute bottom-0 left-0 h-0.5"
        style={{ background: t.color }}
        initial={{ width: "100%" }}
        animate={{ width: 0 }}
        transition={{ duration: 4.2, ease: "linear" }}
      />
    </motion.div>
  );
}

export function ToastHost() {
  const toasts = useHive((s) => s.toasts);
  return (
    <div className="pointer-events-none fixed bottom-28 right-4 z-[120] flex flex-col items-end gap-2.5">
      <AnimatePresence initial={false}>
        {toasts.map((t) => (
          <ToastRow key={t.id} toast={t} />
        ))}
      </AnimatePresence>
    </div>
  );
}
